import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ObrigatorioContexto {
  id: string;
  entidade: string;
  contexto: string;
  campo: string;
  mensagem_erro: string;
  ativo: boolean;
}

export interface ValidationError {
  campo: string;
  mensagem: string;
}

export function useContextValidation() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  /* As regras quase nunca mudam: ficam guardadas 5 min, e o botão que move o
     pedido não vai ao servidor buscá-las a cada clique (Douglas 29/10/2026). */
  const fetchRequiredFields = (entidade: string, contexto: string): Promise<ObrigatorioContexto[]> =>
    queryClient.fetchQuery({
      queryKey: ['obrigatorios-contexto', entidade, contexto],
      staleTime: 5 * 60_000,
      queryFn: () => buscarRequiredFields(entidade, contexto),
    });

  const buscarRequiredFields = async (entidade: string, contexto: string): Promise<ObrigatorioContexto[]> => {
    const { data, error } = await supabase
      .from('obrigatorios_contexto')
      .select('*')
      .eq('entidade', entidade)
      .eq('contexto', contexto)
      .eq('ativo', true);

    if (error) {
      console.error('Erro ao buscar campos obrigatórios:', error);
      return [];
    }

    return data || [];
  };

  const validateRequiredFields = async (
    entidade: string,
    contexto: string,
    data: Record<string, any>
  ): Promise<{ isValid: boolean; errors: ValidationError[] }> => {
    setIsLoading(true);
    
    try {
      const requiredFields = await fetchRequiredFields(entidade, contexto);
      const errors: ValidationError[] = [];

      requiredFields.forEach(rule => {
        const fieldValue = data[rule.campo];
        
        // Verificar se o campo está vazio, nulo ou undefined
        if (!fieldValue || fieldValue === '' || fieldValue === false) {
          errors.push({
            campo: rule.campo,
            mensagem: rule.mensagem_erro
          });
        }
      });

      const isValid = errors.length === 0;

      // Exibir erros via toast se houver
      if (!isValid) {
        errors.forEach(error => {
          toast({
            variant: "destructive",
            title: "Campo obrigatório",
            description: error.mensagem,
          });
        });
      }

      return { isValid, errors };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchRequiredFields,
    validateRequiredFields,
    isLoading
  };
}